import React, { useState } from "react";
import { Lock, Eye, EyeOff, Check } from "lucide-react";
import { api, formatErr } from "../lib/api";

/**
 * ChangePasswordCard — componente riusabile (Customer Dashboard + Business Dashboard).
 * Endpoint: POST /api/me/change-password { current_password, new_password }.
 */
export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: "ok"|"err", text }
  const [open, setOpen] = useState(false);

  function strengthOf(pw) {
    if (!pw) return { score: 0, label: "" };
    let s = 0;
    if (pw.length >= 8) s++;
    if (pw.length >= 12) s++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
    if (/\d/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    const labels = ["Molto debole", "Debole", "Sufficiente", "Buona", "Ottima", "Eccellente"];
    return { score: s, label: labels[s] };
  }

  const strength = strengthOf(next);

  async function submit(e) {
    e.preventDefault();
    setMsg(null);
    if (next.length < 8) {
      setMsg({ type: "err", text: "La nuova password deve avere almeno 8 caratteri." });
      return;
    }
    if (!/[A-Z]/.test(next)) {
      setMsg({ type: "err", text: "Deve contenere almeno una lettera maiuscola." });
      return;
    }
    if (!/\d/.test(next)) {
      setMsg({ type: "err", text: "Deve contenere almeno un numero." });
      return;
    }
    if (next !== confirm) {
      setMsg({ type: "err", text: "Le due password non coincidono." });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setMsg({ type: "ok", text: "Password aggiornata correttamente." });
      setCurrent(""); setNext(""); setConfirm("");
      setTimeout(() => { setOpen(false); setMsg(null); }, 1800);
    } catch (e2) {
      setMsg({ type: "err", text: formatErr(e2.response?.data?.detail) || "Errore" });
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="card-soft p-4 w-full text-left hover:border-zinc-400 transition flex items-center gap-3"
        data-testid="change-password-trigger"
      >
        <div className="h-10 w-10 rounded-xl bg-zinc-900 text-white flex items-center justify-center">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-medium">Cambia password</div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400">Aggiorna le credenziali del tuo account.</div>
        </div>
        <div className="text-xs text-zinc-400 dark:text-zinc-500">→</div>
      </button>
    );
  }

  return (
    <div className="card-soft p-5" data-testid="change-password-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-zinc-900 text-white flex items-center justify-center">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-medium">Cambia password</div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400">Almeno 8 caratteri, una maiuscola e un numero. Diversa dall'attuale.</div>
        </div>
        <button type="button" onClick={() => { setOpen(false); setMsg(null); }} className="text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100" data-testid="change-password-close">
          Chiudi
        </button>
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Password attuale</label>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="input-soft mt-1.5"
            data-testid="change-password-current"
          />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nuova password</label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              required
              minLength={8}
              maxLength={128}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="input-soft mt-1.5 pr-10"
              data-testid="change-password-new"
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-300"
              tabIndex={-1}
              aria-label={showNew ? "Nascondi password" : "Mostra password"}
            >
              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {next && (
            <div className="flex items-center gap-2 mt-1.5" data-testid="change-password-strength">
              <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden dark:bg-zinc-800">
                <div
                  className={`h-full transition-all ${strength.score <= 1 ? "bg-red-500" : strength.score <= 2 ? "bg-amber-500" : strength.score <= 3 ? "bg-yellow-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, strength.score * 20)}%` }}
                />
              </div>
              <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-500 w-20 text-right dark:text-zinc-400">{strength.label}</span>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Conferma nuova password</label>
          <input
            type={showNew ? "text" : "password"}
            required
            minLength={8}
            maxLength={128}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input-soft mt-1.5"
            data-testid="change-password-confirm"
          />
          {confirm && next !== confirm && (
            <div className="text-[11px] text-red-600 mt-1">Le password non coincidono.</div>
          )}
        </div>

        {msg && (
          <div
            className={`text-sm rounded-xl px-3 py-2 ${msg.type === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50" : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50"}`}
            data-testid={`change-password-msg-${msg.type}`}
          >
            {msg.type === "ok" ? <Check className="h-4 w-4 inline mr-1" /> : null}
            {msg.text}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !current || !next || !confirm}
          className="btn-accent w-full disabled:opacity-50"
          data-testid="change-password-submit"
        >
          {busy ? "Aggiornamento…" : "Aggiorna password"}
        </button>
      </form>
    </div>
  );
}
